import { ThreeDSecure as CieloThreeDSecure, getCielo3DSToken, ThreeDSResult as CieloThreeDSResult } from './providers/cielo';
import { PagarmeThreeDSecure, getPagarme3DSToken, PagarmeThreeDSResult, createPagarmeTdsData } from './providers/pagarme';
import { THREE_DS_CONFIG } from './config';

export type ThreeDSProvider = 'cielo' | 'pagarme';

export interface BaseThreeDSResult {
    success: boolean;
    error?: string;
    provider: ThreeDSProvider;
    // Campos específicos do resultado
    cavv?: string | null;
    xid?: string | null;
    eci?: string | null;
    version?: string | null;
    referenceId?: string | null;
    // Campos específicos do Pagar.me
    trans_status?: string;
    tds_server_trans_id?: string;
}

export interface BaseCheckoutData {
    amount: number;
    currency?: string;
    email: string;
    name: string;
    phone: string;
    address: {
        street: string;
        number: string;
        complement?: string;
        city: string;
        state: string;
        zipcode: string;
    };
    paymentMethod: 'credit' | 'debit';
    cardData: {
        holderName: string;
        cardNumber: string;
        cvv: string;
        expMonth: string;
        expYear: string;
    };
}

export interface ThreeDSProviderConfig {
    name: ThreeDSProvider;
    getToken: () => Promise<string>;
    component: React.ComponentType<any>;
    mapCheckoutData: (data: BaseCheckoutData) => any;
    mapResult: (result: any) => BaseThreeDSResult;
}

const tokenCache = new Map<ThreeDSProvider, { token: string; timestamp: number }>();

const getTokenCacheDuration = (provider: ThreeDSProvider): number => {
    switch (provider) {
        case 'pagarme': return 0; // sempre buscar token novo (validade ~20s)
        case 'cielo': return THREE_DS_CONFIG.TIMEOUTS.TOKEN_CACHE.CIELO;
        default: return 30 * 1000;
    }
};

const providerConfigs: Record<ThreeDSProvider, ThreeDSProviderConfig> = {
    cielo: {
        name: 'cielo',
        getToken: getCielo3DSToken,
        component: CieloThreeDSecure,
        mapCheckoutData: (data: BaseCheckoutData) => ({
            accessToken: '', // Será preenchido pelo token
            amount: data.amount,
            orderNumber: `ORDER-${Date.now()}`,
            currency: data.currency || 'BRL',
            paymentMethod: data.paymentMethod,
            cardNumber: data.cardData.cardNumber.replace(/\D/g, ''),
            cardExpirationMonth: data.cardData.expMonth.padStart(2, '0'),
            cardExpirationYear: data.cardData.expYear,
        }),
        mapResult: (result: CieloThreeDSResult): BaseThreeDSResult => ({
            success: !!(result.cavv || result.eci),
            provider: 'cielo',
            cavv: result.cavv,
            xid: result.xid,
            eci: result.eci,
            version: result.version,
            referenceId: result.referenceId,
        })
    },
    pagarme: {
        name: 'pagarme',
        getToken: getPagarme3DSToken,
        component: PagarmeThreeDSecure,
        mapCheckoutData: (data: BaseCheckoutData) => {
            const tdsData = createPagarmeTdsData(data);
            return {
                tdsData,
                cardData: data.cardData
            };
        },
        mapResult: (result: PagarmeThreeDSResult): BaseThreeDSResult => ({
            success: result.trans_status === 'Y' || result.trans_status === 'A',
            provider: 'pagarme',
            error: result.error,
            trans_status: result.trans_status,
            tds_server_trans_id: result.tds_server_trans_id,
            eci: result.trans_status || 'A',
        })
    }
};

export const determineThreeDSProvider = (): ThreeDSProvider => {
    const provider = (import.meta.env.VITE_3DS_PROVIDER || 'cielo').toLowerCase();

    if (provider in providerConfigs) {
        return provider as ThreeDSProvider;
    }

    console.warn(`Provider 3DS inválido: ${provider}. Usando Cielo como padrão.`);
    return 'cielo';
};

export const getThreeDSToken = async (provider: ThreeDSProvider): Promise<string> => {
    const cached = tokenCache.get(provider);
    const cacheDuration = getTokenCacheDuration(provider);

    if (cached && (Date.now() - cached.timestamp) < cacheDuration) {
        return cached.token;
    }

    const config = providerConfigs[provider];
    if (!config) {
        throw new Error(`Provider 3DS não suportado: ${provider}`);
    }

    try {
        const token = await config.getToken();
        if (!token || typeof token !== 'string' || token.trim() === '') {
            throw new Error(`Token 3DS vazio/inválido retornado pelo provider: ${provider}`);
        }
        tokenCache.set(provider, { token, timestamp: Date.now() });
        return token;
    } catch (error) {
        console.error(`Erro ao obter token 3DS para ${provider}:`, error);
        throw error;
    }
};

export const getThreeDSTokenWithRetry = async (
    provider: ThreeDSProvider,
    maxRetries: number = 3
): Promise<string> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await getThreeDSToken(provider);
        } catch (error) {
            if (attempt === maxRetries) {
                throw error;
            }
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
    }
    throw new Error('Max retries exceeded');
};

export const getProviderConfig = (provider: ThreeDSProvider): ThreeDSProviderConfig => {
    const config = providerConfigs[provider];
    if (!config) {
        throw new Error(`Provider 3DS não suportado: ${provider}`);
    }
    return config;
};

export const validateThreeDSData = (data: BaseCheckoutData): void => {
    const requiredFields = ['email', 'name', 'phone', 'address', 'cardData'];
    const missingFields = requiredFields.filter(field =>
        !data[field as keyof BaseCheckoutData]
    );

    if (missingFields.length > 0) {
        throw new Error(`Campos obrigatórios para 3DS: ${missingFields.join(', ')}`);
    }

    const { cardData } = data;
    if (!cardData.cardNumber || cardData.cardNumber.replace(/\D/g, '').length < 13) {
        throw new Error('Número do cartão inválido para 3DS');
    }

    if (!cardData.cvv || cardData.cvv.length < 3) {
        throw new Error('CVV inválido para 3DS');
    }

    if (!cardData.expMonth || !cardData.expYear) {
        throw new Error('Data de expiração inválida para 3DS');
    }

    const { address } = data;
    if (!address.street || !address.city || !address.zipcode) {
        throw new Error('Dados de endereço incompletos para 3DS');
    }
};

export const initialize3DS = async (
  checkoutData: BaseCheckoutData,
  provider?: ThreeDSProvider
): Promise<{
  provider: ThreeDSProvider;
  token: string;
  mappedData: any;
  config: ThreeDSProviderConfig;
}> => {
      const selectedProvider = provider || determineThreeDSProvider();

    validateThreeDSData(checkoutData);

    const config = getProviderConfig(selectedProvider);
    const token = await getThreeDSTokenWithRetry(selectedProvider);

    const mappedData = config.mapCheckoutData(checkoutData);

  return {
    provider: selectedProvider,
    token,
    mappedData,
    config
  };
};

export const clearTokenCache = (provider?: ThreeDSProvider): void => {
    if (provider) {
        tokenCache.delete(provider);
    } else {
        tokenCache.clear();
    }
};

export const registerThreeDSProvider = (
    name: string,
    config: Omit<ThreeDSProviderConfig, 'name'>
): void => {
    (providerConfigs as any)[name] = {
        name,
        ...config
    };
};

export {
    CieloThreeDSecure,
    PagarmeThreeDSecure,
    getCielo3DSToken,
    getPagarme3DSToken,
    createPagarmeTdsData
};
